import { createSlice } from "@reduxjs/toolkit"
import { EncryptStorage } from "encrypt-storage"
import { v4 as uuidv4 } from "uuid"

const encryptedSnippets = new EncryptStorage(
  // eslint-disable-next-line no-undef
  process.env.REACT_APP_ENCRYPT_STORAGE,
  {
    prefix: "@_snippets",
  }
)

const encryptedApp = new EncryptStorage(
  // eslint-disable-next-line no-undef
  process.env.REACT_APP_ENCRYPT_STORAGE,
  {
    prefix: "@_app",
  }
)

const getLsSnippets = encryptedSnippets.getItem("_code")
const setLsSnippets = (snippets) =>
  encryptedSnippets.setItem("_code", snippets)

const getLsFolders = encryptedSnippets.getItem("_folders")
const setLsFolders = (folders) =>
  encryptedSnippets.setItem("_folders", folders)

const getLsCustomSVG = encryptedSnippets.getItem("_custom")
const setLsCustomSVG = (svg) =>
  encryptedSnippets.setItem("_custom", svg)

const getLsInitSnippetSettings = encryptedSnippets.getItem("_init")
const setLsInitSnippetSettings = (init) =>
  encryptedSnippets.setItem("_init", init)

const getLsSettings = encryptedApp.getItem("_settings")
const setLsSettings = (settings) =>
  encryptedApp.setItem("_settings", settings)

const getLsFilter = encryptedApp.getItem("_filter")
const setLsFilter = (filter) =>
  encryptedApp.setItem("_filter", filter)

const initialState = {
  snippets: getLsSnippets ? getLsSnippets : [],

  pinnedSnippets: [],
  othersSnippets: [],
  customSVG: getLsCustomSVG ? getLsCustomSVG : [],
  is: { Deleting: false, TransferringAll: false, Searching: false },
  usage: {
    foldersQty: 0,
    snippetsQty: 0,
    screenshotsQty: 0,
  },
  folders: getLsFolders
    ? getLsFolders
    : [
        {
          title: "HTML",
          id: uuidv4(),
          defaultPosition: { x: 0, y: 120 },
        },
        {
          title: "SASS",
          id: uuidv4(),
          defaultPosition: { x: 85, y: 120 },
        },
        {
          title: "Typescript",
          id: uuidv4(),
          defaultPosition: { x: 170, y: 120 },
        },
      ],
  filter: getLsFilter
    ? getLsFilter
    : {
        folder: "",
        bookmarkColor: "",
        type: "",
        flagged: "",
        starred: "",
        search: "",
        searchHistory: [],
      },
  snippetsTabsValue: 1,

  settings: getLsSettings
    ? getLsSettings
    : {
        deleteSingleMessage: true,
        deleteAllMessage: true,
        transferAllMessages: true,
        hideEmptyFolders: false,
        openFolderAfterSnippetMove: false,
        showCreatedSnippetTimeDistance: true,
        showSnippifyCreditsLogo: true,
        snippetsIconsTabsValue: 0,
        mode: "system default",
      },
  initialSnippetSettings: getLsInitSnippetSettings
    ? getLsInitSnippetSettings
    : {
        editorSettings: {
          theme: "Snippify Dark",
          fontSize: 13,
          guttersFontSize: 14,
          lineHeight: 7,
          foldGutterWidth: 24,
          guttersPaddingLeft: 16,
          contentPadding: { x: 16, y: 16 },
          tabSize: 2,
          foldGutter: false,
          guttersBoxShadow: false,
          lineWrapping: false,
          lineNumbers: false,
          fullscreen: false,
          cursor: "Line",
          lang: "tsx",
        },
        screenShotSettings: {
          screenShotPadding: { x: 30, y: 60 },
          screenShotBGColor: "#90c5cc",
          screenshotWidth: 900,
          screenshotScale: 2,
        },
        windowSettings: {
          windowBorderRadius: 7,
          windowBoxShadowOffset: { x: -15, y: 5 },
          windowBoxShadowBlur: 30,
          windowBoxShadowSpread: -3,
          windowBoxShadowColor: "#1a1a1a",
          windowDimensions: { w: 400, h: 400 },
          windowBorderWidth: 0,
          windowBorderColor: "#ffffff",
          windowTitleBarSettings: {
            windowTitleBarAlign: "center",
            windowTitleBarFormats: ["italic"],
            windowTitleBarTextColor: "#b641ac",
            windowTitleBarFontSize: 13,
            windowTitleBarShowSnippetTitle: true,
            windowTitleBarShowSnippetType: true,
            windowTitleBarStyle: "Mac",
            windowTitleBarHeight: 45,
            windowTitleBarPaddingX: 20,

            windowTitleBarBgColor: "#ffffff",
            windowTitleBarWidth: 100,
            windowTitleBarBorderBottomRadius: "0",
            windowTitleBarBottomSpacing: "7",
            windowTitleBarParentBgColor: "#b2ff00",

            windowTitleBarBoxShadowBlur: 30,
            windowTitleBarBoxShadowSpread: -3,
            windowTitleBarBoxShadowColor: "#00000000",
            windowTitleBarBoxShadowOffset: { x: -15, y: 5 },
          },
        },
      },
}

const snippetsSlice = createSlice({
  name: "snippets",
  initialState,
  reducers: {
    addSnippet: (state, action) => {
      state.snippets = [{ ...action.payload }, ...state.snippets]

      // localStorage.setItem("snippets", JSON.stringify(state.snippets))
      setLsSnippets(state.snippets)
    },
    deleteSnippet: (state, action) => {
      state.snippets = state.snippets.filter(
        (item) => item.id !== action.payload
      )
      setLsSnippets(state.snippets)
    },
    saveSnippet: (state, action) => {
      const { id, type, folder, title, body } = action.payload
      state.snippets.filter((snippet) => {
        snippet.id === id
          ? ((snippet.type = type),
            (snippet.folder = folder),
            (snippet.title = title),
            (snippet.body = body),
            (snippet.lastEdit = new Date().toISOString()))
          : snippet
      })
      setLsSnippets(state.snippets)
    },
    togglePinnedSnippet: (state, action) => {
      state.snippets.find((item) => {
        if (item.id === action.payload) {
          item.isPinned = !item.isPinned
          item.lastEdit = new Date().toISOString()
        }

        setLsSnippets(state.snippets)
      })
    },
    addToDescriptions: (state, action) => {
      const currentSnippet = state.snippets.find(
        (snippet) => snippet.id === action.payload?.id
      )
      if (currentSnippet) {
        currentSnippet.descriptions = [
          { ...action.payload.description },
          ...currentSnippet.descriptions,
        ].filter((discription) => discription.text !== "")
      }

      setLsSnippets(state.snippets)
    },
    editDescription: (state, action) => {
      const { id, descriptionId, descriptionText } = action.payload
      const currentSnippet = state.snippets.find(
        (snippet) => snippet.id === id
      )
      const currentDescription = currentSnippet
        ? currentSnippet.descriptions.find(
            (description) => description.id === descriptionId
          )
        : null

      if (currentDescription) {
        currentDescription.text = descriptionText
      }
      setLsSnippets(state.snippets)
    },
    descriptionPositionUpdater: (state, action) => {
      const { id, descriptionId, x, y, dimension } = action.payload
      const currentSnippet = state.snippets.find(
        (snippet) => snippet.id === id
      )
      const currentDescription = currentSnippet
        ? currentSnippet.descriptions.find(
            (description) => description.id === descriptionId
          )
        : null

      if (currentDescription) {
        currentDescription.position = { x, y }
      }
      if (currentDescription && dimension) {
        currentDescription.dimension = dimension
      }
      setLsSnippets(state.snippets)
    },
    toggleDescriptionLock: (state, action) => {
      const { id, descriptionId } = action.payload
      const currentSnippet = state.snippets.find(
        (snippet) => snippet.id === id
      )

      const currentDescription = currentSnippet
        ? currentSnippet.descriptions.find(
            (description) => description.id === descriptionId
          )
        : null
      if (currentDescription) {
        currentDescription.isLocked = !currentDescription.isLocked
      }

      setLsSnippets(state.snippets)
    },
    deleteDescription: (state, action) => {
      const { id, descriptionId } = action.payload
      const currentSnippet = state.snippets.find(
        (snippet) => snippet.id === id
      )
      currentSnippet.descriptions =
        currentSnippet.descriptions.filter(
          (description) => description.id !== descriptionId
        )

      setLsSnippets(state.snippets)
    },
    deleteAllDescriptions: (state, action) => {
      const { id } = action.payload
      const currentSnippet = state.snippets.find(
        (snippet) => snippet.id === id
      )
      const { length } = currentSnippet.descriptions
      if (length) {
        currentSnippet.descriptions = []
      }
      setLsSnippets(state.snippets)
    },

    createNewFolder: (state, action) => {
      const isExisting = state.folders.some(
        (item) => item.title === action.payload?.title
      )
      if (!isExisting) {
        state.folders = [action.payload, ...state.folders].filter(
          (folder) => folder !== null
        )
      }

      setLsFolders(state.folders)
    },
    resetSnippetFolder: (state, action) => {
      const isExist = state.snippets.find(
        (snippet) => snippet.id === action.payload.id
      )
      if (isExist) {
        isExist.folder = null
      }
      setLsSnippets(state.snippets)
    },
    resetSnippetType: (state, action) => {
      const isExist = state.snippets.find(
        (snippet) => snippet.id === action.payload.id
      )
      if (isExist) {
        isExist.type = null
      }
      setLsSnippets(state.snippets)
    },
    toggleSnippetFlag: (state, action) => {
      state.snippets.find((item) => {
        if (item.id === action.payload) {
          item.flagged = !item.flagged
          item.lastEdit = new Date().toISOString()
        }
        setLsSnippets(state.snippets)
      })
    },
    toggleSnippetStar: (state, action) => {
      state.snippets.find((item) => {
        if (item.id === action.payload) {
          item.starred = !item.starred
          item.lastEdit = new Date().toISOString()
        }

        setLsSnippets(state.snippets)
      })
    },
    toggleSnippetLock: (state, action) => {
      const { id, isLocked } = action.payload
      const currentSnippet = state.snippets.find(
        (snippet) => snippet.id === id
      )
      if (currentSnippet) {
        currentSnippet.isSnippetLocked =
          isLocked === "lock" ? true : false
      }
      currentSnippet.descriptions.forEach(
        (description) =>
          (description.isLocked = isLocked === "lock" ? true : false)
      )

      setLsSnippets(state.snippets)
    },

    setSnippetBookmarkColor: (state, action) => {
      const { id, bookmarkColor } = action.payload
      state.snippets.find((snippet) => {
        if (snippet.id === id) {
          snippet.bookmarkColor = bookmarkColor
          snippet.lastEdit = new Date().toISOString()
          setLsSnippets(state.snippets)
        }
      })
    },
    setFlaggedFilter: (state, action) => {
      state.filter.flag = action.payload
      setLsFilter(state.filter)
    },
    setStarredFilter: (state, action) => {
      state.filter.starred = action.payload
      setLsFilter(state.filter)
    },
    setTypeFilter: (state, action) => {
      state.filter.type =
        state.filter.type === action.payload ? "" : action.payload
      setLsFilter(state.filter)
    },

    setPinnedOthersTabsValue: (state, action) => {
      state.snippetsTabsValue = action.payload
    },

    setBookmarkColorFilter: (state, action) => {
      state.filter.bookmarkColor =
        state.filter.bookmarkColor === action.payload
          ? ""
          : action.payload
      setLsFilter(state.filter)
    },
    setFolderFilter: (state, action) => {
      state.filter.folder =
        state.filter.folder === action.payload ? "" : action.payload
      setLsFilter(state.filter)
    },

    deleteFolder: (state, action) => {
      const folders = [...action.payload]
      const foldersNames = folders.map((folder) => folder?.title)
      state.folders = state.folders.filter(
        (folder) => !foldersNames.includes(folder.title)
      )

      state.snippets.forEach((snippet) =>
        foldersNames.includes(snippet.folder?.title)
          ? (snippet.folder = null)
          : snippet
      )

      if (foldersNames.includes(state.filter.folder)) {
        state.filter.folder = ""
      }

      setLsFolders(state.folders)
      setLsSnippets(state.snippets)
      setLsFilter(state.filter)
    },
    editFolderName: (state, action) => {
      const { title, id } = action.payload
      const target = state.folders.find((folder) => folder.id === id)
      const isCurrBreadcrumbs = state.filter.folder === target?.title

      if (isCurrBreadcrumbs && target.title !== title.trim()) {
        state.filter.folder = title.trim()
      }

      if (target && target.title !== title.trim()) {
        target.title = title.trim()
        state.snippets.forEach((snippet) => {
          if (snippet.folder?.id === id) {
            snippet.folder.title = title.trim()
          }
        })
      } else {
        return
      }

      setLsFolders(state.folders)
      setLsSnippets(state.snippets)
    },

    toggleCmdkPopup: (state) => {
      state.is.Searching = !state.is.Searching
    },

    setSearchFilter: (state, action) => {
      state.filter.search = action.payload
      setLsFilter(state.filter)
    },
    resetSearchFilter: (state) => {
      state.filter.search = ""
      setLsFilter(state.filter)
    },
    resetFilter: (state) => {
      state.filter.bookmarkColor = ""
      state.filter.flag = ""
      state.filter.folder = ""
      state.filter.type = ""
      state.filter.search = ""
      state.filter.starred = ""
      state.snippetsTabsValue = 1
      setLsFilter(state.filter)
    },
    pinAllCurrentSnippets: (state) => {
      const currentOthersSnippetsIds = state.othersSnippets.map(
        (snippet) => snippet.id
      )
      state.snippets.filter((snippet) =>
        currentOthersSnippetsIds.includes(snippet.id)
          ? (snippet.isPinned = !snippet.isPinned)
          : snippet
      )

      setLsSnippets(state.snippets)
    },
    unPinAllCurrentSnippets: (state) => {
      const currentPinnedSnippetsIds = state.pinnedSnippets.map(
        (snippet) => snippet.id
      )
      state.snippets.filter((snippet) =>
        currentPinnedSnippetsIds.includes(snippet.id)
          ? (snippet.isPinned = !snippet.isPinned)
          : snippet
      )

      setLsSnippets(state.snippets)
    },
    deleteAllCurrentPinnedSnippets: (state) => {
      const currentPinnedSnippetsIds = state.pinnedSnippets.map(
        (snippet) => snippet.id
      )

      // const filteredSnippets
      state.snippets = state.snippets.filter(
        (snippet) => !currentPinnedSnippetsIds.includes(snippet.id)
      )

      // state.snippets = [...filteredSnippets]

      setLsSnippets(state.snippets)
    },
    deleteAllCurrentOthersSnippets: (state) => {
      const currentOthersSnippetsIds = state.othersSnippets.map(
        (snippet) => snippet.id
      )
      state.snippets = state.snippets.filter(
        (snippet) => !currentOthersSnippetsIds.includes(snippet.id)
      )

      setLsSnippets(state.snippets)
    },
    setPinnedSnippets: (state, action) => {
      state.pinnedSnippets = [...action.payload]
    },
    setOthersSnippets: (state, action) => {
      state.othersSnippets = [...action.payload]
    },
    toggleIsDeletingSnippetsPopupDialog: (state) => {
      state.is.Deleting = !state.is.Deleting
    },
    toggleIsTransferringAllSnippetsPopupDialog: (state) => {
      state.is.TransferringAll = !state.is.TransferringAll
    },

    toggleConfirmMessageSwitchSettings: (state, action) => {
      if (action.payload === "deleteSingle") {
        state.settings.deleteSingleMessage =
          !state.settings.deleteSingleMessage
      }
      if (action.payload === "deleteAll") {
        state.settings.deleteAllMessage =
          !state.settings.deleteAllMessage
      }
      if (action.payload === "transfer") {
        state.settings.transferAllMessages =
          !state.settings.transferAllMessages
      }
      setLsSettings(state.settings)
    },
    toggleOpenFolderAfterSnippetMove: (state) => {
      state.settings.openFolderAfterSnippetMove =
        !state.settings.openFolderAfterSnippetMove
      setLsSettings(state.settings)
    },
    toggleShowSnippifyCreditsLogo: (state) => {
      state.settings.showSnippifyCreditsLogo =
        !state.settings.showSnippifyCreditsLogo
      setLsSettings(state.settings)
    },
    toggleShowDistanceCreatedTime: (state) => {
      state.settings.showCreatedSnippetTimeDistance =
        !state.settings.showCreatedSnippetTimeDistance
      setLsSettings(state.settings)
    },
    toggleThemeMode: (state, action) => {
      state.settings.mode = action.payload
      setLsSettings(state.settings)
    },
    addToSearchHistory: (state, action) => {
      const isExist = state.filter.searchHistory.some(
        (history) => history.keyword === action.payload.keyword
      )
      if (!isExist && action.payload.keyword !== "") {
        state.filter.searchHistory = [
          action.payload,
          ...state.filter.searchHistory,
        ].slice(0, 10)
      }

      setLsFilter(state.filter)
    },
    deleteFromSearchHistory: (state, action) => {
      state.filter.searchHistory = state.filter.searchHistory.filter(
        (history) => history.keyword !== action.payload
      )
      setLsFilter(state.filter)
    },
    folderPositionUpdater: (state, action) => {
      const { x, y, id } = action.payload
      const isExist = state.folders.find((folder) => folder.id === id)
      if (isExist) {
        isExist.defaultPosition = { x, y }
      }
      setLsFolders(state.folders)
    },
    toggleHideEmptyFolders: (state) => {
      state.settings.hideEmptyFolders =
        !state.settings.hideEmptyFolders
      setLsSettings(state.settings)
    },
    setSnippetsIconsTabsValue: (state, action) => {
      state.settings.snippetsIconsTabsValue = action.payload
      setLsSettings(state.settings)
    },

    toggleButtonsEditorSettings: (state, action) => {
      const {
        id,
        lineWrapping,
        lineNumbers,
        fullscreen,
        foldGutter,
        guttersBoxShadow,
        init,
      } = action.payload
      const currentSnippet = state.snippets.find(
        (snippet) => snippet.id === id
      )

      if (lineWrapping) {
        init
          ? (state.initialSnippetSettings.editorSettings.lineWrapping =
              !state.initialSnippetSettings.editorSettings
                .lineWrapping)
          : (currentSnippet.settings.editorSettings.lineWrapping =
              !currentSnippet.settings.editorSettings.lineWrapping)
      }
      if (lineNumbers) {
        init
          ? (state.initialSnippetSettings.editorSettings.lineNumbers =
              !state.initialSnippetSettings.editorSettings
                .lineNumbers)
          : (currentSnippet.settings.editorSettings.lineNumbers =
              !currentSnippet.settings.editorSettings.lineNumbers)
      }
      if (fullscreen) {
        currentSnippet.settings.editorSettings.fullscreen =
          !currentSnippet.settings.editorSettings.fullscreen
      }
      if (foldGutter) {
        init
          ? (state.initialSnippetSettings.editorSettings.foldGutter =
              !state.initialSnippetSettings.editorSettings.foldGutter)
          : (currentSnippet.settings.editorSettings.foldGutter =
              !currentSnippet.settings.editorSettings.foldGutter)
      }
      if (guttersBoxShadow) {
        init
          ? (state.initialSnippetSettings.editorSettings.guttersBoxShadow =
              !state.initialSnippetSettings.editorSettings
                .guttersBoxShadow)
          : (currentSnippet.settings.editorSettings.guttersBoxShadow =
              !currentSnippet.settings.editorSettings
                .guttersBoxShadow)
      }
      init
        ? setLsInitSnippetSettings(state.initialSnippetSettings)
        : setLsSnippets(state.snippets)
    },
    setScreenShotSettings: (state, action) => {
      const {
        id,
        x,
        y,
        screenShotBGColor,
        screenshotWidth,
        screenshotScale,
        init,
      } = action.payload
      const currentSnippet = state.snippets.find(
        (snippet) => snippet.id === id
      )

      if (x) {
        init
          ? (state.initialSnippetSettings.screenShotSettings.screenShotPadding.x =
              x)
          : (currentSnippet.settings.screenShotSettings.screenShotPadding.x =
              x)
      }
      if (y) {
        init
          ? (state.initialSnippetSettings.screenShotSettings.screenShotPadding.y =
              y)
          : (currentSnippet.settings.screenShotSettings.screenShotPadding.y =
              y)
      }
      if (screenShotBGColor) {
        init
          ? (state.initialSnippetSettings.screenShotSettings.screenShotBGColor =
              screenShotBGColor)
          : (currentSnippet.settings.screenShotSettings.screenShotBGColor =
              screenShotBGColor)
      }
      if (screenshotWidth) {
        init
          ? (state.initialSnippetSettings.screenShotSettings.screenshotWidth =
              screenshotWidth)
          : (currentSnippet.settings.screenShotSettings.screenshotWidth =
              screenshotWidth)
      }
      if (screenshotScale) {
        init
          ? (state.initialSnippetSettings.screenShotSettings.screenshotScale =
              screenshotScale)
          : (currentSnippet.settings.screenShotSettings.screenshotScale =
              screenshotScale)
      }

      init
        ? setLsInitSnippetSettings(state.initialSnippetSettings)
        : setLsSnippets(state.snippets)
    },
    setWindowSettings: (state, action) => {
      const {
        id,
        windowBorderRadius,
        windowBoxShadowBlur,
        windowBoxShadowSpread,
        windowBoxShadowColor,
        windowBoxShadowOffsetX,
        windowBoxShadowOffsetY,
        windowDimensions,
        windowBorderWidth,
        windowBorderColor,
        init,
      } = action.payload
      const currentSnippet = state.snippets.find(
        (snippet) => snippet.id === id
      )

      if (windowDimensions) {
        currentSnippet.settings.windowSettings.windowDimensions =
          windowDimensions
      }

      if (windowBoxShadowOffsetX) {
        init
          ? (state.initialSnippetSettings.windowSettings.windowBoxShadowOffset.x =
              windowBoxShadowOffsetX)
          : (currentSnippet.settings.windowSettings.windowBoxShadowOffset.x =
              windowBoxShadowOffsetX)
      }
      if (windowBoxShadowOffsetY) {
        init
          ? (state.initialSnippetSettings.windowSettings.windowBoxShadowOffset.y =
              windowBoxShadowOffsetY)
          : (currentSnippet.settings.windowSettings.windowBoxShadowOffset.y =
              windowBoxShadowOffsetY)
      }

      if (windowBorderRadius) {
        init
          ? (state.initialSnippetSettings.windowSettings.windowBorderRadius =
              windowBorderRadius)
          : (currentSnippet.settings.windowSettings.windowBorderRadius =
              windowBorderRadius)
      }
      if (windowBoxShadowBlur) {
        init
          ? (state.initialSnippetSettings.windowSettings.windowBoxShadowBlur =
              windowBoxShadowBlur)
          : (currentSnippet.settings.windowSettings.windowBoxShadowBlur =
              windowBoxShadowBlur)
      }
      if (windowBoxShadowSpread) {
        init
          ? (state.initialSnippetSettings.windowSettings.windowBoxShadowSpread =
              windowBoxShadowSpread)
          : (currentSnippet.settings.windowSettings.windowBoxShadowSpread =
              windowBoxShadowSpread)
      }
      if (windowBoxShadowColor) {
        init
          ? (state.initialSnippetSettings.windowSettings.windowBoxShadowColor =
              windowBoxShadowColor)
          : (currentSnippet.settings.windowSettings.windowBoxShadowColor =
              windowBoxShadowColor)
      }

      if (windowBorderWidth) {
        init
          ? (state.initialSnippetSettings.windowSettings.windowBorderWidth =
              windowBorderWidth)
          : (currentSnippet.settings.windowSettings.windowBorderWidth =
              windowBorderWidth)
      }
      if (windowBorderColor) {
        init
          ? (state.initialSnippetSettings.windowSettings.windowBorderColor =
              windowBorderColor)
          : (currentSnippet.settings.windowSettings.windowBorderColor =
              windowBorderColor)
      }

      init
        ? setLsInitSnippetSettings(state.initialSnippetSettings)
        : setLsSnippets(state.snippets)
    },
    setWindowTitleBarSettings: (state, action) => {
      const {
        id,
        windowTitleBarStyle,
        windowTitleBarFormats,
        windowTitleBarAlign,
        windowTitleBarTextColor,
        windowTitleBarFontSize,
        windowTitleBarHeight,
        windowTitleBarPaddingX,
        windowTitleBarBgColor,
        windowTitleBarShowSnippetTitle,
        windowTitleBarShowSnippetType,
        windowTitleBarWidth,
        windowTitleBarBorderBottomRadius,
        windowTitleBarBottomSpacing,
        windowTitleBarParentBgColor,
        windowTitleBarBoxShadowBlur,
        windowTitleBarBoxShadowSpread,
        windowTitleBarBoxShadowColor,
        windowTitleBarBoxShadowOffsetX,
        windowTitleBarBoxShadowOffsetY,
        init,
      } = action.payload
      const currentSnippet = state.snippets.find(
        (snippet) => snippet.id === id
      )

      if (windowTitleBarStyle) {
        init
          ? (state.initialSnippetSettings.windowSettings.windowTitleBarSettings.windowTitleBarStyle =
              windowTitleBarStyle)
          : (currentSnippet.settings.windowSettings.windowTitleBarSettings.windowTitleBarStyle =
              windowTitleBarStyle)
      }
      if (windowTitleBarFormats) {
        currentSnippet.settings.windowSettings.windowTitleBarSettings.windowTitleBarFormats =
          windowTitleBarFormats
      }
      if (windowTitleBarAlign) {
        currentSnippet.settings.windowSettings.windowTitleBarSettings.windowTitleBarAlign =
          windowTitleBarAlign
      }
      if (windowTitleBarTextColor) {
        currentSnippet.settings.windowSettings.windowTitleBarSettings.windowTitleBarTextColor =
          windowTitleBarTextColor
      }
      if (windowTitleBarFontSize) {
        currentSnippet.settings.windowSettings.windowTitleBarSettings.windowTitleBarFontSize =
          windowTitleBarFontSize
      }
      if (windowTitleBarHeight) {
        init
          ? (state.initialSnippetSettings.windowSettings.windowTitleBarSettings.windowTitleBarHeight =
              windowTitleBarHeight)
          : (currentSnippet.settings.windowSettings.windowTitleBarSettings.windowTitleBarHeight =
              windowTitleBarHeight)
      }
      if (windowTitleBarPaddingX) {
        init
          ? (state.initialSnippetSettings.windowSettings.windowTitleBarSettings.windowTitleBarPaddingX =
              windowTitleBarPaddingX)
          : (currentSnippet.settings.windowSettings.windowTitleBarSettings.windowTitleBarPaddingX =
              windowTitleBarPaddingX)
      }
      if (windowTitleBarBgColor) {
        init
          ? (state.initialSnippetSettings.windowSettings.windowTitleBarSettings.windowTitleBarBgColor =
              windowTitleBarBgColor)
          : (currentSnippet.settings.windowSettings.windowTitleBarSettings.windowTitleBarBgColor =
              windowTitleBarBgColor)
      }

      if (windowTitleBarShowSnippetTitle) {
        init
          ? (state.initialSnippetSettings.windowSettings.windowTitleBarSettings.windowTitleBarShowSnippetTitle =
              !state.initialSnippetSettings.windowSettings
                .windowTitleBarSettings
                .windowTitleBarShowSnippetTitle)
          : (currentSnippet.settings.windowSettings.windowTitleBarSettings.windowTitleBarShowSnippetTitle =
              !currentSnippet.settings.windowSettings
                .windowTitleBarSettings
                .windowTitleBarShowSnippetTitle)
      }
      if (windowTitleBarShowSnippetType) {
        init
          ? (state.initialSnippetSettings.windowSettings.windowTitleBarSettings.windowTitleBarShowSnippetType =
              !state.initialSnippetSettings.windowSettings
                .windowTitleBarSettings.windowTitleBarShowSnippetType)
          : (currentSnippet.settings.windowSettings.windowTitleBarSettings.windowTitleBarShowSnippetType =
              !currentSnippet.settings.windowSettings
                .windowTitleBarSettings.windowTitleBarShowSnippetType)
      }
      if (windowTitleBarWidth) {
        init
          ? (state.initialSnippetSettings.windowSettings.windowTitleBarSettings.windowTitleBarWidth =
              windowTitleBarWidth)
          : (currentSnippet.settings.windowSettings.windowTitleBarSettings.windowTitleBarWidth =
              windowTitleBarWidth)
      }
      if (windowTitleBarBorderBottomRadius) {
        init
          ? (state.initialSnippetSettings.windowSettings.windowTitleBarSettings.windowTitleBarBorderBottomRadius =
              windowTitleBarBorderBottomRadius)
          : (currentSnippet.settings.windowSettings.windowTitleBarSettings.windowTitleBarBorderBottomRadius =
              windowTitleBarBorderBottomRadius)
      }
      if (windowTitleBarBottomSpacing) {
        init
          ? (state.initialSnippetSettings.windowSettings.windowTitleBarSettings.windowTitleBarBottomSpacing =
              windowTitleBarBottomSpacing)
          : (currentSnippet.settings.windowSettings.windowTitleBarSettings.windowTitleBarBottomSpacing =
              windowTitleBarBottomSpacing)
      }
      if (windowTitleBarParentBgColor) {
        init
          ? (state.initialSnippetSettings.windowSettings.windowTitleBarSettings.windowTitleBarParentBgColor =
              windowTitleBarParentBgColor)
          : (currentSnippet.settings.windowSettings.windowTitleBarSettings.windowTitleBarParentBgColor =
              windowTitleBarParentBgColor)
      }
      if (windowTitleBarBoxShadowBlur) {
        init
          ? (state.initialSnippetSettings.windowSettings.windowTitleBarSettings.windowTitleBarBoxShadowBlur =
              windowTitleBarBoxShadowBlur)
          : (currentSnippet.settings.windowSettings.windowTitleBarSettings.windowTitleBarBoxShadowBlur =
              windowTitleBarBoxShadowBlur)
      }
      if (windowTitleBarBoxShadowSpread) {
        init
          ? (state.initialSnippetSettings.windowSettings.windowTitleBarSettings.windowTitleBarBoxShadowSpread =
              windowTitleBarBoxShadowSpread)
          : (currentSnippet.settings.windowSettings.windowTitleBarSettings.windowTitleBarBoxShadowSpread =
              windowTitleBarBoxShadowSpread)
      }
      if (windowTitleBarBoxShadowColor) {
        init
          ? (state.initialSnippetSettings.windowSettings.windowTitleBarSettings.windowTitleBarBoxShadowColor =
              windowTitleBarBoxShadowColor)
          : (currentSnippet.settings.windowSettings.windowTitleBarSettings.windowTitleBarBoxShadowColor =
              windowTitleBarBoxShadowColor)
      }
      if (windowTitleBarBoxShadowOffsetX) {
        init
          ? (state.initialSnippetSettings.windowSettings.windowTitleBarSettings.windowTitleBarBoxShadowOffset.x =
              windowTitleBarBoxShadowOffsetX)
          : (currentSnippet.settings.windowSettings.windowTitleBarSettings.windowTitleBarBoxShadowOffset.x =
              windowTitleBarBoxShadowOffsetX)
      }
      if (windowTitleBarBoxShadowOffsetY) {
        init
          ? (state.initialSnippetSettings.windowSettings.windowTitleBarSettings.windowTitleBarBoxShadowOffset.y =
              windowTitleBarBoxShadowOffsetY)
          : (currentSnippet.settings.windowSettings.windowTitleBarSettings.windowTitleBarBoxShadowOffset.y =
              windowTitleBarBoxShadowOffsetY)
      }

      init
        ? setLsInitSnippetSettings(state.initialSnippetSettings)
        : setLsSnippets(state.snippets)
    },
    setEditorSettings: (state, action) => {
      const {
        id,
        theme,
        fontSize,
        guttersFontSize,
        lineHeight,
        foldGutterWidth,
        guttersPaddingLeft,
        contentPaddingX,
        contentPaddingY,
        tabSize,
        cursor,
        lang,
        init,
      } = action.payload
      const currentSnippet = state.snippets.find(
        (snippet) => snippet.id === id
      )
      if (theme) {
        init
          ? (state.initialSnippetSettings.editorSettings.theme =
              theme)
          : (currentSnippet.settings.editorSettings.theme = theme)
      }
      if (lang) {
        init
          ? (state.initialSnippetSettings.editorSettings.lang = lang)
          : (currentSnippet.settings.editorSettings.lang = lang)
      }
      if (fontSize) {
        init
          ? (state.initialSnippetSettings.editorSettings.fontSize =
              fontSize)
          : (currentSnippet.settings.editorSettings.fontSize =
              fontSize)
      }
      if (guttersFontSize) {
        currentSnippet.settings.editorSettings.guttersFontSize =
          guttersFontSize
      }
      if (lineHeight) {
        init
          ? (state.initialSnippetSettings.editorSettings.lineHeight =
              lineHeight)
          : (currentSnippet.settings.editorSettings.lineHeight =
              lineHeight)
      }
      if (foldGutterWidth) {
        init
          ? (state.initialSnippetSettings.editorSettings.foldGutterWidth =
              foldGutterWidth)
          : (currentSnippet.settings.editorSettings.foldGutterWidth =
              foldGutterWidth)
      }
      if (guttersPaddingLeft) {
        init
          ? (state.initialSnippetSettings.editorSettings.guttersPaddingLeft =
              guttersPaddingLeft)
          : (currentSnippet.settings.editorSettings.guttersPaddingLeft =
              guttersPaddingLeft)
      }
      if (contentPaddingX) {
        init
          ? (state.initialSnippetSettings.editorSettings.contentPadding.x =
              contentPaddingX)
          : (currentSnippet.settings.editorSettings.contentPadding.x =
              contentPaddingX)
      }
      if (contentPaddingY) {
        init
          ? (state.initialSnippetSettings.editorSettings.contentPadding.y =
              contentPaddingY)
          : (currentSnippet.settings.editorSettings.contentPadding.y =
              contentPaddingY)
      }
      if (tabSize) {
        init
          ? (state.initialSnippetSettings.editorSettings.tabSize =
              tabSize)
          : (currentSnippet.settings.editorSettings.tabSize = tabSize)
      }
      if (cursor) {
        init
          ? (state.initialSnippetSettings.editorSettings.cursor =
              cursor)
          : (currentSnippet.settings.editorSettings.cursor = cursor)
      }
      init
        ? setLsInitSnippetSettings(state.initialSnippetSettings)
        : setLsSnippets(state.snippets)
    },

    addDescriptionToCustomList: (state, action) => {
      state.customSVG = [{ ...action.payload }, ...state.customSVG]

      setLsCustomSVG(state.customSVG)
    },
    deleteCustomListItem: (state, action) => {
      state.customSVG = state.customSVG.filter(
        (item) => item.id !== action.payload.id
      )
      setLsCustomSVG(state.customSVG)
    },
    setUsage: (state, action) => {
      const { foldersQty, snippetsQty, screenshotsQty } =
        action.payload
      if (foldersQty) {
        state.usage.foldersQty = foldersQty
      }
      if (snippetsQty) {
        state.usage.snippetsQty = snippetsQty
      }
      if (screenshotsQty) {
        state.usage.screenshotsQty = screenshotsQty
      }
    },
  },
})

export const {
  addSnippet,
  createNewFolder,
  toggleSnippetFlag,
  toggleSnippetStar,
  toggleSnippetLock,
  deleteSnippet,
  saveSnippet,
  setSnippetBookmarkColor,
  togglePinnedSnippet,
  setFlaggedFilter,
  setStarredFilter,
  setBookmarkColorFilter,
  setTypeFilter,
  setFolderFilter,
  setSearchFilter,
  setPinnedOthersTabsValue,
  deleteFolder,
  editFolderName,
  toggleCmdkPopup,
  resetSearchFilter,
  resetFilter,
  pinAllCurrentSnippets,
  unPinAllCurrentSnippets,
  deleteAllCurrentPinnedSnippets,
  deleteAllCurrentOthersSnippets,
  setPinnedSnippets,
  setOthersSnippets,
  toggleIsDeletingSnippetsPopupDialog,
  toggleIsTransferringAllSnippetsPopupDialog,
  toggleConfirmMessageSwitchSettings,
  toggleHideEmptyFolders,
  toggleOpenFolderAfterSnippetMove,
  toggleShowSnippifyCreditsLogo,
  toggleShowDistanceCreatedTime,
  toggleThemeMode,
  resetSnippetFolder,
  resetSnippetType,
  addToSearchHistory,
  deleteFromSearchHistory,
  folderPositionUpdater,
  addToDescriptions,
  editDescription,
  descriptionPositionUpdater,
  setSnippetsIconsTabsValue,
  setScreenShotSettings,
  toggleButtonsEditorSettings,
  setWindowSettings,
  setWindowTitleBarSettings,
  setEditorSettings,
  toggleDescriptionLock,
  deleteDescription,
  deleteAllDescriptions,
  addDescriptionToCustomList,
  deleteCustomListItem,
  setUsage,
} = snippetsSlice.actions
export const snippets = (state) => state.snippets.snippets
export const customSVG = (state) => state.snippets.customSVG
export const folders = (state) => state.snippets.folders
export const bookmarkColor = (state) =>
  state.snippets.filter.bookmarkColor
export const flag = (state) => state.snippets.filter.flag
export const starred = (state) => state.snippets.filter.starred
export const type = (state) => state.snippets.filter.type
export const folder = (state) => state.snippets.filter.folder
export const search = (state) => state.snippets.filter.search
export const searchHistory = (state) =>
  state.snippets.filter.searchHistory
export const snippetsTabsValue = (state) =>
  state.snippets.snippetsTabsValue
export const pinnedSnippets = (state) => state.snippets.pinnedSnippets
export const othersSnippets = (state) => state.snippets.othersSnippets
export const isDeleting = (state) => state.snippets.is.Deleting
export const isTransferringAll = (state) =>
  state.snippets.is.TransferringAll
export const isSearching = (state) => state.snippets.is.Searching

export const deleteSingleMessage = (state) =>
  state.snippets.settings.deleteSingleMessage

export const openFolderAfterSnippetMove = (state) =>
  state.snippets.settings.openFolderAfterSnippetMove

export const showCreatedSnippetTimeDistance = (state) =>
  state.snippets.settings.showCreatedSnippetTimeDistance

export const deleteAllMessage = (state) =>
  state.snippets.settings.deleteAllMessage
export const transferAllMessages = (state) =>
  state.snippets.settings.transferAllMessages

export const mode = (state) => state.snippets.settings.mode

export const hideEmptyFolders = (state) =>
  state.snippets.settings.hideEmptyFolders

export const snippetsIconsTabsValue = (state) =>
  state.snippets.settings.snippetsIconsTabsValue

export const showSnippifyCreditsLogo = (state) =>
  state.snippets.settings.showSnippifyCreditsLogo

export const initialSnippetSettings = (state) =>
  state.snippets.initialSnippetSettings

export const usage = (state) => state.snippets.usage

export default snippetsSlice.reducer
